// components/Chat/ZenoToolTrace.jsx
//
// Per-response record of the tools Zeno ran to produce an answer. The backend
// surfaces them on the message metadata as `toolsUsed` (streaming, camelCased)
// or `tools_used` (non-streaming), each item shaped { name, args, is_error }.
// Rendered as a compact, collapsible footnote under the assistant bubble so the
// user can see *what* Zeno did without it crowding the reply.
import React, { useState } from "react";
import { Wrench, ChevronDown, Check, AlertTriangle } from "lucide-react";

// list_projects -> "List projects"
function humanize(name) {
  if (!name) return "tool";
  const s = String(name).replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Compact one-line preview of the tool arguments, truncated so long values
// (URLs, prompts) don't blow up the row.
function argsPreview(args) {
  if (!args || typeof args !== "object") return "";
  const entries = Object.entries(args).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  if (!entries.length) return "";
  return entries
    .map(([k, v]) => {
      let val = typeof v === "string" ? v : JSON.stringify(v);
      if (typeof val === "string" && val.length > 40) val = val.slice(0, 39) + "…";
      return `${k}: ${val}`;
    })
    .join(" · ");
}

// Accepts either the camelCase (stream) or snake_case (non-stream) list.
export default function ZenoToolTrace({ tools }) {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(tools) ? tools.filter((t) => t && t.name) : [];
  if (!list.length) return null;

  const anyError = list.some((t) => t.is_error);

  return (
    <div className="w-fit max-w-full rounded-xl border border-white/10 bg-white/[0.03]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-400 transition-colors hover:text-gray-200"
      >
        <Wrench className="h-3.5 w-3.5 flex-shrink-0 text-[#b98bff]" />
        <span className="font-medium">
          {list.length === 1 ? "Used 1 tool" : `Used ${list.length} tools`}
        </span>
        {anyError && <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-400" />}
        <ChevronDown
          className={`ml-1 h-3.5 w-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul className="space-y-1.5 border-t border-white/10 px-3 py-2">
          {list.map((t, i) => {
            const preview = argsPreview(t.args);
            return (
              <li key={i} className="flex items-start gap-2 text-xs">
                <span
                  className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full ${
                    t.is_error
                      ? "bg-red-500/15 text-red-300"
                      : "bg-emerald-500/15 text-emerald-300"
                  }`}
                >
                  {t.is_error ? (
                    <AlertTriangle className="h-2.5 w-2.5" />
                  ) : (
                    <Check className="h-2.5 w-2.5" />
                  )}
                </span>
                <div className="min-w-0">
                  <span className="font-medium text-gray-200">{humanize(t.name)}</span>
                  {preview && (
                    <span className="ml-1.5 break-all text-gray-500">{preview}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
